import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2, Play, CheckCircle, XCircle, Clock, RefreshCw,
  TrendingUp, FileText, Mail, Megaphone, BarChart3, Users, Search,
  Facebook, Instagram, Linkedin, Youtube, Calendar, Edit3, Save, CalendarDays,
} from 'lucide-react';
import SEO from '@/components/SEO';

const SUPABASE_URL = 'https://plizcqmxjmtzdgmmhtxj.supabase.co';

interface MarketingOutput {
  id: string;
  task_id: string;
  agent_name: string;
  agent_role: string;
  title: string;
  content: string;
  output_type: string;
  status: string;
  reviewed_by: string | null;
  review_notes: string | null;
  reviewed_at: string | null;
  run_duration_ms: number | null;
  error: string | null;
  created_at: string;
}

const AGENT_ICONS: Record<string, typeof TrendingUp> = {
  Dana: BarChart3,
  Casey: FileText,
  Alex: Search,
  Riley: Mail,
  Pat: Megaphone,
};

const PLATFORM_META: Record<string, { icon: typeof Facebook; label: string; color: string }> = {
  facebook:           { icon: Facebook,  label: 'Facebook',                      color: 'bg-blue-100 text-blue-700' },
  instagram:          { icon: Instagram, label: 'Instagram',                     color: 'bg-pink-100 text-pink-700' },
  linkedin:           { icon: Linkedin,  label: 'LinkedIn',                      color: 'bg-amber-100 text-amber-800' },
  linkedin_personal:  { icon: Linkedin,  label: 'LinkedIn, Brian (Personal)',   color: 'bg-amber-100 text-amber-800' },
  linkedin_company:   { icon: Linkedin,  label: 'LinkedIn, SeniorThrive (Co)', color: 'bg-sky-100 text-sky-700' },
  youtube:            { icon: Youtube,   label: 'YouTube',                       color: 'bg-red-100 text-red-700' },
};

const AGENT_COLORS: Record<string, string> = {
  Dana: 'bg-emerald-100 text-emerald-800',
  Casey: 'bg-blue-100 text-blue-800',
  Alex: 'bg-purple-100 text-purple-800',
  Riley: 'bg-amber-100 text-amber-800',
  Pat: 'bg-rose-100 text-rose-800',
};

const STATUS_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  pending: { variant: 'outline', label: 'Pending Review' },
  approved: { variant: 'default', label: 'Approved' },
  rejected: { variant: 'destructive', label: 'Rejected' },
  published: { variant: 'secondary', label: 'Published' },
};

const TASKS = [
  { id: 'dana-daily-pulse', agent: 'Dana', label: 'Daily Pulse', schedule: 'M-F 8:30am' },
  { id: 'casey-weekly-batch', agent: 'Casey', label: 'Weekly Social Batch (5 days + 1 video)', schedule: 'Sun 6:00pm' },
  { id: 'casey-daily-social', agent: 'Casey', label: 'One-off Daily Social', schedule: 'Manual only (cron disabled)' },
  { id: 'alex-weekly-seo-brief', agent: 'Alex', label: 'SEO Brief', schedule: 'Mon 8:00am' },
  { id: 'casey-weekly-content', agent: 'Casey', label: 'Content Pack', schedule: 'Mon 9:00am' },
  { id: 'riley-weekly-digest', agent: 'Riley', label: 'Email Digest', schedule: 'Fri 2:00pm' },
  { id: 'dana-weekly-financials', agent: 'Dana', label: 'Weekly Financials', schedule: 'Fri 3:30pm' },
  { id: 'pat-weekly-team-review', agent: 'Pat', label: 'Team Review', schedule: 'Fri 5:00pm' },
];

export default function MarketingDashboard() {
  const [outputs, setOutputs] = useState<MarketingOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [runningTask, setRunningTask] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchOutputs = useCallback(async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('marketing_outputs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      toast({ title: 'Error loading outputs', description: error.message, variant: 'destructive' });
    } else {
      setOutputs((data as MarketingOutput[]) || []);
    }
    setIsLoading(false);
  }, [toast]);

  useEffect(() => { fetchOutputs(); }, [fetchOutputs]);

  const runTask = async (taskId: string) => {
    setRunningTask(taskId);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/marketing-cron`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId }),
      });
      const result = await res.json();
      if (result.ok) {
        toast({ title: `${result.agent} completed`, description: result.title });
        fetchOutputs();
      } else {
        toast({ title: 'Task failed', description: result.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Failed to run task', description: err.message, variant: 'destructive' });
    }
    setRunningTask(null);
  };

  const approveAndExecute = async (id: string) => {
    // Call the marketing-execute Edge Function which approves AND runs the action
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/marketing-execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ output_id: id }),
      });
      const result = await res.json();
      if (result.ok) {
        setOutputs(prev => prev.map(o => o.id === id ? { ...o, status: result.action === 'executed' ? 'published' : 'approved', review_notes: result.result, reviewed_at: new Date().toISOString() } : o));
        toast({ title: `${result.agent}, ${result.action}`, description: result.result?.slice(0, 100) });
      } else {
        toast({ title: 'Execution failed', description: result.error, variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const updateStatus = async (id: string, status: string) => {
    if (status === 'approved') {
      // Approve = execute
      return approveAndExecute(id);
    }
    const { error } = await supabase
      .from('marketing_outputs')
      .update({ status, reviewed_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setOutputs(prev => prev.map(o => o.id === id ? { ...o, status, reviewed_at: new Date().toISOString() } : o));
      toast({ title: `Marked as ${status}` });
    }
  };

  // Split outputs into today vs history
  const today = new Date().toISOString().slice(0, 10);
  const todayOutputs = outputs.filter(o => o.created_at?.slice(0, 10) === today);
  const historyOutputs = outputs.filter(o => o.created_at?.slice(0, 10) !== today);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return ', ';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <>
      <SEO title="Marketing Dashboard, Admin" noIndex />

      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Marketing Team</h1>
            <p className="text-muted-foreground">AI marketing automation outputs and approvals</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="default" asChild>
              <Link to="/admin/marketing/week">
                <CalendarDays className="h-4 w-4 mr-2" />
                Open Weekly Social Review
              </Link>
            </Button>
            <Button variant="outline" onClick={fetchOutputs} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Manual Trigger Cards */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Run a Task
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {TASKS.map(task => {
                const Icon = AGENT_ICONS[task.agent] || FileText;
                const color = AGENT_COLORS[task.agent] || 'bg-gray-100 text-gray-800';
                const isRunning = runningTask === task.id;
                return (
                  <Button
                    key={task.id}
                    variant="outline"
                    className="h-auto p-4 flex flex-col items-start gap-2 text-left"
                    disabled={!!runningTask}
                    onClick={() => runTask(task.id)}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div className={`p-1.5 rounded-md ${color}`}>
                        {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                      </div>
                      <span className="font-medium text-sm">{task.agent}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{task.label}</span>
                    <span className="text-xs text-muted-foreground/60">{task.schedule}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tabs: Today / History */}
        <Tabs defaultValue="today">
          <TabsList>
            <TabsTrigger value="today">
              Today {todayOutputs.length > 0 && <Badge variant="secondary" className="ml-2">{todayOutputs.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="history">
              History {historyOutputs.length > 0 && <Badge variant="outline" className="ml-2">{historyOutputs.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-4 mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : todayOutputs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-3 opacity-50" />
                  <p>No outputs yet today. Run a task above or wait for the next scheduled run.</p>
                </CardContent>
              </Card>
            ) : (
              todayOutputs.map(output => (
                <OutputCard key={output.id} output={output} onApprove={id => updateStatus(id, 'approved')} onReject={id => updateStatus(id, 'rejected')} formatTime={formatTime} formatDuration={formatDuration} />
              ))
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            {historyOutputs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <p>No historical outputs yet.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyOutputs.map(o => {
                      const badge = STATUS_BADGES[o.status] || STATUS_BADGES.pending;
                      return (
                        <TableRow key={o.id}>
                          <TableCell>
                            <Badge className={AGENT_COLORS[o.agent_name] || 'bg-gray-100 text-gray-800'}>
                              {o.agent_name}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{o.title}</TableCell>
                          <TableCell><Badge variant={badge.variant}>{badge.label}</Badge></TableCell>
                          <TableCell className="text-muted-foreground">{formatDuration(o.run_duration_ms)}</TableCell>
                          <TableCell className="text-muted-foreground">{formatTime(o.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// Individual output card with content preview + inline edit + approve/reject
function OutputCard({ output, onApprove, onReject, formatTime, formatDuration }: {
  output: MarketingOutput;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  formatTime: (d: string) => string;
  formatDuration: (ms: number | null) => string;
}) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const Icon = AGENT_ICONS[output.agent_name] || FileText;
  const color = AGENT_COLORS[output.agent_name] || 'bg-gray-100 text-gray-800';
  const badge = STATUS_BADGES[output.status] || STATUS_BADGES.pending;

  // Parse JSON social content if present
  const socialPlatforms: Record<string, string> | null = (() => {
    try {
      const parsed = JSON.parse(output.content);
      if (typeof parsed === 'object' && !Array.isArray(parsed) && (parsed.facebook || parsed.instagram || parsed.linkedin)) {
        return parsed as Record<string, string>;
      }
    } catch { /* not JSON */ }
    return null;
  })();

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [plainEdit, setPlainEdit] = useState<string>(output.content);

  const isBatch = output.task_id === 'casey-weekly-batch';
  const batchId = (output as any).metadata?.batch_id;

  const saveSocialEdits = async () => {
    if (!socialPlatforms) return;
    setSaving(true);
    // Merge edits back into parsed JSON, then update marketing_outputs + re-run RPC
    const merged = { ...socialPlatforms, ...edits };
    const newJson = JSON.stringify(merged, null, 2);
    const { error } = await supabase.from('marketing_outputs').update({ content: newJson }).eq('id', output.id);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
      setSaving(false);
      return;
    }
    // Re-parse into social_content_queue (idempotent; preserves scheduled_for from metadata)
    try {
      await fetch(`https://plizcqmxjmtzdgmmhtxj.supabase.co/rest/v1/rpc/parse_casey_to_queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: (supabase as any).supabaseKey, Authorization: `Bearer ${(supabase as any).supabaseKey}` },
        body: JSON.stringify({ p_output_id: output.id }),
      });
    } catch { /* non-fatal */ }
    setEditing(false);
    setEdits({});
    setSaving(false);
    toast({ title: 'Saved — queue re-synced' });
  };

  const savePlainEdit = async () => {
    setSaving(true);
    const { error } = await supabase.from('marketing_outputs').update({ content: plainEdit }).eq('id', output.id);
    setSaving(false);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
      return;
    }
    setEditing(false);
    toast({ title: 'Saved' });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{output.title}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {output.agent_name}, {output.agent_role} · {formatTime(output.created_at)} · {formatDuration(output.run_duration_ms)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isBatch && (
              <Button variant="outline" size="sm" asChild>
                <Link to={batchId ? `/admin/marketing/week?batch=${batchId}` : '/admin/marketing/week'}>
                  <Calendar className="h-4 w-4 mr-1" /> Review week
                </Link>
              </Button>
            )}
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Content: JSON social rendered per platform (editable), else plain text */}
        {socialPlatforms ? (
          <div className="space-y-3 bg-muted/50 rounded-lg p-4">
            {['linkedin', 'linkedin_personal', 'linkedin_company', 'facebook', 'instagram', 'youtube'].filter(k => socialPlatforms[k]).map(key => {
              const meta = PLATFORM_META[key] || PLATFORM_META.facebook;
              const PlatIcon = meta.icon;
              const value = edits[key] !== undefined ? edits[key] : socialPlatforms[key];
              return (
                <div key={key} className="space-y-1.5">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.color}`}>
                    <PlatIcon className="h-3 w-3" /> {meta.label}
                  </span>
                  {editing ? (
                    <Textarea
                      value={value}
                      onChange={(e) => setEdits(prev => ({ ...prev, [key]: e.target.value }))}
                      rows={Math.min(10, Math.max(3, Math.ceil(value.length / 60)))}
                      className="text-sm"
                    />
                  ) : (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap pl-1">{value}</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {editing ? (
              <Textarea
                value={plainEdit}
                onChange={(e) => setPlainEdit(e.target.value)}
                rows={Math.min(20, Math.max(6, Math.ceil(plainEdit.length / 80)))}
                className="text-sm"
              />
            ) : (
              <>
                <div className={`text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-4 ${expanded ? '' : 'max-h-32 overflow-hidden relative'}`}>
                  {output.content}
                  {!expanded && output.content.length > 300 && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-muted/80 to-transparent" />
                  )}
                </div>
                {output.content.length > 300 && (
                  <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Show less' : 'Show full output'}
                  </Button>
                )}
              </>
            )}
          </>
        )}

        {output.error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-lg p-3">
            Error: {output.error}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          {!editing && output.status === 'pending' && !isBatch && (
            <>
              <Button size="sm" onClick={() => onApprove(output.id)} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="h-4 w-4 mr-1" /> Approve &amp; Execute
              </Button>
              <Button size="sm" variant="outline" onClick={() => onReject(output.id)}>
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Edit3 className="h-4 w-4 mr-1" /> Edit
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button size="sm" onClick={socialPlatforms ? saveSocialEdits : savePlainEdit} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save edits
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEdits({}); setPlainEdit(output.content); }}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
